<template>
  <div :class="['tir-pm-svg', { 'with-bg': withBg }]" :style="style">
    <template v-if="url">
      <div class="tir-pm-svg__content" v-html="svgContent" v-if="svgContent" />
    </template>
    <template v-else>
      <div class="tir-pm-svg__content">
        <slot />
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch, computed, CSSProperties } from "vue";

// #region defineProps
const props = defineProps({
  /**Ссылка на svg */
  url: { type: String },
  /**Цвет */
  color: { type: String },
  /**Цвет */
  backgroundColor: { type: String },
  /**Включить заливку */
  isFill: { type: Boolean, default: true },
});
// #endregion defineProps

// #region refs
/**Контент svg иконки */
const svgContent = ref<string | null>(null);
// #endregion refs

// #region computed
/**С фоном */
const withBg = computed(
  () => props.isFill && (props.backgroundColor || props.color)
);

const style = computed<CSSProperties>(() => {
  const obj: CSSProperties = {};
  if (props.color) {
    obj["--tir-pm-svg__bg-color"] = props.isFill
      ? props.backgroundColor ?? props.color
      : undefined;
    obj["--tir-pm-svg__bg-opacity"] = props.isFill
      ? props.backgroundColor
        ? "1"
        : "0.10"
      : "0";
    obj["--tir-pm-svg__icon-color"] = props.color;
  }
  return obj;
});
// #endregion computed

// #region watch
watch(() => props.url, loadSvg);
// #endregion watch

// #region Хуки жизненного цикла
onMounted(() => {
  loadSvg();
});
// #endregion Хуки жизненного цикла

// #region Функции
async function loadSvg() {
  if (props.url)
    try {
      // Проверяем наличие SVG в sessionStorage
      const cachedSvg = sessionStorage.getItem(props.url);
      if (cachedSvg) {
        svgContent.value = cachedSvg;
        return;
      }

      // Загружаем SVG с сервера
      const response = await fetch(props.url);

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const text = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(text, "image/svg+xml");
      const svg = doc.querySelector("svg");
      if (!svg) {
        throw new Error("Invalid SVG format");
      }

      // Сохраняем в sessionStorage и обновляем значение
      sessionStorage.setItem(props.url, text);
      svgContent.value = text;
    } catch (error) {
      console.error("Error fetching SVG:", error);
    }
}
// #endregion Функции
</script>

<style lang="scss" scoped>
.tir-pm-svg {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1em;
  height: 1em;

  &__content {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 1em;
    height: 1em;
    color: var(--tir-pm-svg__icon-color);
  }

  &.with-bg {
    .tir-pm-svg__content {
      z-index: 2;
      width: calc(1em - var(--tir-pm-svg__padding) * 2);
      height: calc(1em - var(--tir-pm-svg__padding) * 2);
    }

    &::before {
      content: "";
      display: block;
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      right: 0;
      background-color: var(--tir-pm-svg__bg-layer-color);
      border-radius: var(--tir-pm-svg__br);
    }
    &::after {
      content: "";
      display: block;
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      right: 0;
      z-index: 1;
      background-color: var(--tir-pm-svg__bg-color);
      opacity: var(--tir-pm-svg__bg-opacity);
      border-radius: var(--tir-pm-svg__br);
    }
  }
}
</style>
